
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { CivicEducationItem, transformContentForDatabase, transformDatabaseContent } from '@/types/civic-education';
import { createUniqueId } from '@/lib/utils';

export const useContentOperations = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState<CivicEducationItem[]>([]);

  // Fetch content when the hook is first used
  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('civic_education_content')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      if (data && data.length > 0) {
        const transformedContent = data.map(item => transformDatabaseContent(item));
        setItems(transformedContent);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load civic education content');
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveContent = async (item: CivicEducationItem, isEditing: boolean) => {
    try {
      const dbItem = transformContentForDatabase(item);
      
      if (isEditing) {
        const { error } = await supabase
          .from('civic_education_content')
          .update(dbItem)
          .eq('id', item.id);
        
        if (error) throw error;
        
        setItems(items.map(i => i.id === item.id ? item : i));
        toast.success('Content updated successfully');
      } else {
        const { data, error } = await supabase
          .from('civic_education_content')
          .insert(dbItem)
          .select();
        
        if (error) throw error;
        
        if (data && data[0]) {
          const newItem = transformDatabaseContent(data[0]);
          setItems([newItem, ...items]);
        }
        
        toast.success('New content created successfully');
      }
      
      return true;
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Failed to save content');
      return false;
    }
  };

  const deleteContent = async (id: string) => {
    try {
      const { error } = await supabase
        .from('civic_education_content')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      setItems(items.filter(item => item.id !== id));
      toast.success('Content deleted successfully');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete content');
    }
  };

  return {
    items,
    isLoading,
    fetchContent,
    saveContent,
    deleteContent
  };
};
